import React, { useRef, useState } from 'react';
import { Alert, Button, Container, Form, Spinner, Table } from 'react-bootstrap';
import PageHeader from 'components/PageHeader';
import NaceKodeApi, { ImportNaceKoderResult } from 'services/KjederegisteretAdminApi/NaceKodeApi';

const NaceKodeImportPage: React.FC = () => {
  const [version, setVersion] = useState('SN2025-09');
  const [customVersion, setCustomVersion] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportNaceKoderResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const effectiveVersion = version === '__custom__' ? customVersion.trim() : version;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !effectiveVersion) return;

    setLoading(true);
    setResult(null);
    setError(null);

    try {
      const res = await NaceKodeApi.import(effectiveVersion, file);
      setResult(res);
      // Reset file input
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (err: any) {
      setError(err?.response?.data || err?.message || 'Ukjent feil under import.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="mt-3" style={{ maxWidth: 600 }}>
      <PageHeader>Importer NACE-koder</PageHeader>
      <p className="text-muted">
        Last opp en CSV-fil fra SSB med NACE-koder. Eksisterende koder for valgt versjon oppdateres;
        nye koder legges til.
      </p>

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Registerversjon</Form.Label>
          <Form.Select value={version} onChange={(e) => setVersion(e.target.value)}>
            <option value="SN2025-09">SN2025-09 – gjeldende (gyldig fra sep. 2025)</option>
            <option value="SN2007">SN2007 – forrige versjon (jan. 2009 – sep. 2025)</option>
            <option value="SN2002">SN2002 – eldre versjon (jan. 2002 – jan. 2009)</option>
            <option value="SN1994">SN1994 – eldre versjon (jan. 1994 – jan. 2002)</option>
            <option value="__custom__">Annen versjon…</option>
          </Form.Select>
        </Form.Group>

        {version === '__custom__' && (
          <Form.Group className="mb-3">
            <Form.Label>Versjonsnavn</Form.Label>
            <Form.Control
              type="text"
              placeholder="F.eks. SN2025"
              value={customVersion}
              onChange={(e) => setCustomVersion(e.target.value)}
            />
          </Form.Group>
        )}

        <Form.Group className="mb-3">
          <Form.Label>CSV-fil</Form.Label>
          <Form.Control
            ref={fileRef}
            type="file"
            accept=".csv"
            onChange={(e) => {
              const input = e.target as HTMLInputElement;
              setFile(input.files?.[0] ?? null);
            }}
          />
          <Form.Text className="text-muted">
            Semikolon- eller kommaseparert, UTF-8 eller Windows-1252. Kolonner: Code, ParentCode,
            Level, ShortName, Notes (rekkefølge fleksibel, overskriftsrad påkrevd).
          </Form.Text>
        </Form.Group>

        <Button type="submit" disabled={!file || !effectiveVersion || loading} variant="primary">
          {loading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
          {loading ? 'Importerer…' : 'Start import'}
        </Button>
      </Form>

      {error && (
        <Alert variant="danger" className="mt-3">
          {error}
        </Alert>
      )}

      {result && (
        <Alert variant="success" className="mt-3">
          <Alert.Heading>Import fullført – {effectiveVersion}</Alert.Heading>
          <Table size="sm" borderless className="mb-0 mt-2">
            <tbody>
              <tr>
                <td>Nye koder lagt til</td>
                <td>
                  <strong>{result.inserted}</strong>
                </td>
              </tr>
              <tr>
                <td>Eksisterende koder oppdatert</td>
                <td>
                  <strong>{result.updated}</strong>
                </td>
              </tr>
              <tr>
                <td>Rader hoppet over (tomme koder)</td>
                <td>
                  <strong>{result.skipped}</strong>
                </td>
              </tr>
            </tbody>
          </Table>
        </Alert>
      )}
    </Container>
  );
};

export default NaceKodeImportPage;
